import React, { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Scale, Shield } from 'lucide-react';
import Login from './components/Login';
import { onAuthChange, logoutUser, isRegistrationInProgress, onRegistrationProgressChange, LOCAL_SIGNOUT_FLAG, releaseRegistrationHold } from './services/authService';
import { SIGNED_OUT_ALL_EVENT } from './components/SignOutAllDevices';
import { getCases} from './services/caseService';
import { initGoogleCalendar, restoreGoogleCalendarToken, refreshGoogleCalendarToken, setCurrentUserId} from './services/calendarService';
import { getMeetings} from './services/meetingService';
import { getEnhancedTasks} from './services/enhancedTaskService';
import { getFirm, migrateLegacySubscription} from './services/firmService';
import { getFirmUsers, listMyPendingInvites } from './services/userService';
import PendingInvitations, { lockedInvitesPlaceholder } from './components/PendingInvitations';
import FirmUnavailable from './components/FirmUnavailable';
import { SuspendedAccess, NoFirmAccess, SessionRetry, SessionBusy } from './components/SessionScreens';
import { collection, doc, query, where, onSnapshot, terminate, clearIndexedDbPersistence } from 'firebase/firestore';
import { db } from './firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import useOffline, { offlineCacheKey, clearCache as clearOfflineCache, clearOfflineQueue } from './hooks/useOffline';
import useSessionGate from './session/useSessionGate';
import { loadFirmData } from './session/loadFirmData';
import { SESSION } from './session/sessionModel';
import { MFA, mfaRequirement, hasSecondFactor, mfaGateBlocks } from './session/mfaPolicy';
import MfaEnrollment from './components/MfaEnrollment';
import { multiFactor, getIdTokenResult } from 'firebase/auth';
import { authTimeFromClaims } from './session/sessionEpoch';
import { SESSION_EVENTS, postSessionEvent, subscribeSessionEvents } from './session/sessionChannel';
import { cleanupNeeded, getDeviceTenant, setDeviceTenant, runSessionCleanup, guardedReload, stashRecoveryError, takeRecoveryError, tenantFingerprint } from './session/sessionCleanup';
import useWindowSize from './hooks/useWindowSize';
import useSessionTimeout from './hooks/useSessionTimeout';
import { PLANS, checkSubscription, getPlanFeatures } from './utils/subscription';
import { compareDueDatesForSort } from './utils/firmassistTime';
import OfflineBanner from './components/OfflineBanner';
import ErrorBoundary from './components/ErrorBoundary';
import UpgradePrompt from './components/UpgradePrompt';
import ToastContainer from './components/ToastContainer';
import Dashboard from './components/Dashboard';
import BottomTabBar from './components/BottomTabBar';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ExpiredSubscriptionBanner from './components/ExpiredSubscriptionBanner';
import TrialBanner from './components/TrialBanner';
import TrialTierSelectionModal from './components/TrialTierSelectionModal';
import { toast, registerToastHandler, unregisterToastHandler } from './utils/toast';
import { SubscriptionContext } from './contexts/SubscriptionContext';
import { identifyUser, trackTabView, trackSessionStart, trackLogout} from './services/analyticsService';

// Heavy tab components are code-split — each loads on demand instead of in the main bundle
const AuditTrailView = lazy(() => import('./components/AuditTrailView'));
const ReportsExport = lazy(() => import('./components/ReportsExport'));
const Analytics = lazy(() => import('./components/Analytics'));
const DocumentManagement = lazy(() => import('./components/DocumentManagement'));
const LegalResearch = lazy(() => import('./components/LegalResearch'));
const Billing = lazy(() => import('./components/Billing'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const TeamHub = lazy(() => import('./components/TeamHub'));
const TaskCalendar = lazy(() => import('./components/TaskCalendar'));
const ClientManagement = lazy(() => import('./components/ClientManagement'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const ConflictCheck = lazy(() => import('./components/ConflictCheck'));
const CaseManagement = lazy(() => import('./components/CaseManagement'));
const Paralegal = lazy(() => import('./components/Paralegal'));
const CasePlaybookTracker = lazy(() => import('./components/CasePlaybookTracker'));
const ClientStatusPage = lazy(() => import('./components/ClientStatusPage'));
const DocumentTemplates = lazy(() => import('./components/DocumentTemplates'));
const BorrowRequests = lazy(() => import('./components/BorrowRequests'));
const SubscriptionManager = lazy(() => import('./components/SubscriptionManager'));

// Shown while a code-split tab chunk downloads
const TabLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#a8adb7', fontSize: '0.9rem' }}>
    <span className="loading-spinner" style={{ width: '20px', height: '20px', border: '2px solid rgba(212,168,83,0.2)', borderTopColor: '#d4a853', borderRadius: '50%', display: 'inline-block', marginRight: '0.6rem', animation: 'spin 0.8s linear infinite' }} />
    Loading…
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ============================================================================
// SECURITY: HTML SANITIZER (DOMPurify-lite for dangerouslySetInnerHTML)
// ============================================================================
// eslint-disable-next-line no-unused-vars
const purifyHTML = (dirty) => {
  if (!dirty || typeof dirty !== 'string') return '';
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  // Remove all script tags
  doc.querySelectorAll('script, iframe, object, embed, form, input, textarea, link, meta, base, svg').forEach(el => el.remove());
  // Remove event handlers from all elements
  doc.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => {
      if (attr.name.startsWith('on') || attr.value.includes('javascript:')) el.removeAttribute(attr.name);
    });
    // Remove dangerous attributes
    ['srcdoc', 'data', 'action', 'formaction', 'xlink:href'].forEach(a => el.removeAttribute(a));
  });
  return doc.body.innerHTML;
};

// useWindowSize and useSessionTimeout extracted to src/hooks/

// messaging utils (smartWhatsApp, openWhatsApp, smartEmail, smartSend) → src/utils/messaging.js
// subscription utils (PLANS, checkSubscription, getPlanFeatures, initPaystack, checkLimit) → src/utils/subscription.js

const functions = getFunctions(); // still needed by SuperAdminCorpusPanel and AdminPanel

// PLANS, checkSubscription, getPlanFeatures → src/utils/subscription.js

/**
 * Upgrade prompt component — shown when a locked feature is accessed
 */

// ============================================================================
// SUBSCRIPTION MANAGEMENT COMPONENT (Admin Panel)
// ============================================================================

// ============================================================================
// TRIAL TIER SELECTION MODAL — Session 16
// ----------------------------------------------------------------------------
// Full-screen, non-dismissible. Renders when firmProfile.subscription.status
// === 'pending_selection'. User picks Solo, Firm, or Enterprise → writes
// { plan, status: 'trialing', selectedAt } to Firestore and unlocks the app.
// ============================================================================

// ============================================================================
// TRIAL BANNER — Session 16
// ----------------------------------------------------------------------------
// Persistent banner shown at the top of main content for trialing firms.
// Displays current tier, days left, and Switch Tier action.
// Color escalates as Dec 31 approaches: gold → orange → red.
// Defensively wrapped — never throws into the shell.
// ============================================================================



// ============================================================================
// EXPORT UTILITIES
// ============================================================================

// exportToCSV and exportToPDF extracted to src/utils/export.js

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================



// ============================================================================
// MOBILE: Bottom Tab Bar — replaces sidebar on phones
// ============================================================================


// ============================================================
// CATEGORY AND COURT CONFIGS — see imports at top of file
// ============================================================

// ============================================================
// COMPONENT
// ============================================================




// ============================================================================
// FEATURE COMPONENTS
// ============================================================================











// ═══════════════════════════════════════════════════════════════
// PROCEDURAL CASE PLAYBOOKS — Living compliance trackers
// ═══════════════════════════════════════════════════════════════





// ENHANCED CASE MANAGEMENT COMPONENT
// Replace the CaseManagement component in your App.js with this













// ============================================================================
// MAIN APP COMPONENT
// ============================================================================







// ============================================================================
// SESSION GATE WIRING (session & auth-state handling, 2026-10-06)
// ----------------------------------------------------------------------------
// The live profile / firm listeners and the device-cleanup primitives the
// canonical session gate (src/session/) runs on. See sessionModel.js for the
// ACTIVE invariant and sessionCleanup.js for what is cleared on the device.
// ============================================================================
// Neutral notice when a session ends outside this tab's own sign-out (Phase A).
const SESSION_ENDED_NOTICE = 'Your session has ended. Please sign in again.';
const sessionGateDeps = {
  onAuthChange,
  isRegistrationInProgress,
  onRegistrationChange: onRegistrationProgressChange,
  // Phase B: the current ID token's auth_time (seconds), compared with the live
  // profile's sessionsValidAfter by the session model.
  getAuthTime: async (user) => authTimeFromClaims((await getIdTokenResult(user)).claims),
  subscribeDoc: (path, onNext, onError) => onSnapshot(doc(db, path), { includeMetadataChanges: true },
    (snap) => onNext({ exists: snap.exists(), data: snap.exists() ? { id: snap.id, ...snap.data() } : null, fromCache: snap.metadata.fromCache }),
    onError),
};
const sessionCleanupDeps = {
  db,
  terminate,
  clearIndexedDbPersistence,
  signOut: logoutUser,
  clearOfflineCache,
  clearOfflineQueue,
  post: postSessionEvent,
  reload: () => window.location.reload(),
};
const isPermissionDeniedError = (err) => /permission-denied|PERMISSION_DENIED/i.test(String((err && (err.code || err.message)) || ''));

const FirmAssist = () => {
  // ── Canonical session gate: the ONLY authority on whether the shell may render ──
  const session = useSessionGate(sessionGateDeps);
  const sessionStatus = session.status;
  // null | 'running' | 'reload-required' — device cleanup after logout / access change
  const [cleanupPhase, setCleanupPhase] = useState(null);
  const isActiveSession = sessionStatus === SESSION.ACTIVE && !cleanupPhase;
  // Latest generation / activity for async work started under an older one (loadData guard).
  const sessionGenRef = useRef(session.generation);
  sessionGenRef.current = session.generation;
  const isActiveRef = useRef(isActiveSession);
  isActiveRef.current = isActiveSession;
  const sessionStatusRef = useRef(sessionStatus);
  sessionStatusRef.current = sessionStatus;
  const sessionUidRef = useRef(null);
  sessionUidRef.current = session.authUser ? session.authUser.uid : null;
  // The shell's user object (same shape as before) — exists ONLY in ACTIVE.
  const user = useMemo(() => {
    if (!isActiveSession || !session.authUser || !session.profile) return null;
    const fbUser = session.authUser;
    const profile = session.profile;
    return {
      ...fbUser,
      firmId: profile.firmId,
      role: profile.role,
      permissions: profile.permissions,
      displayName: profile.displayName || fbUser.displayName,
      photoURL: profile.photoURL || '',
      userProfile: profile,
    };
  }, [isActiveSession, session.authUser, session.profile]);
  const [authRecoveryError, setAuthRecoveryError] = useState(() => takeRecoveryError());
  const [activeTab, setActiveTab] = useState('dashboard');
  // Onboarding checklist "Set Up"/"Connect" deep-link (ONB-08/ONB-09): tells
  // AdminPanel which of its own tabs/sections to open on next mount, without
  // duplicating the personal-settings UI or changing which controls a role
  // can see — AdminPanel's existing tab gating is untouched.
  const [adminOnboardingDeepLink, setAdminOnboardingDeepLink] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const windowWidth = useWindowSize();
  const isMobile = windowWidth <= 768;
  const [notifications] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [preselectedCase, setPreselectedCase] = useState(null);

  // Service worker management — unregister stale SWs, clear caches, then re-register
  useEffect(() => { trackTabView(activeTab); }, [activeTab]);
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // First: unregister any existing service workers and clear all caches
      // This ensures fresh CSP headers and assets are always loaded from the server
      (async () => {
        try {
          // Clear all caches
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          
          // Unregister all service workers
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
          
          if (registrations.length > 0 || cacheNames.length > 0) {
            console.log(`[App] Cleared ${cacheNames.length} caches, unregistered ${registrations.length} service workers`);
          }
        } catch (e) {
          console.log('[App] Cache/SW cleanup error (non-critical):', e.message);
        }
      })();

      // Note: Service worker disabled for now to prevent stale cache issues
      // FCM push notifications still work via firebase-messaging-sw.js (registered separately below)
    }
  }, []);

  // Cross-component data refresh trigger — increment to force child components to re-fetch
  const [dataVersion, setDataVersion] = useState(0);
  const triggerDataRefresh = React.useCallback(() => setDataVersion(v => v + 1), []);

  // SECURITY: Session timeout — auto-logout after 45 min inactivity
  // Runs through the canonical logout cleanup; the notice is shown on the
  // sign-in screen after the cleanup reload (it used to be a blocking alert()).
  const startCleanupRef = useRef(null);
  const handleSessionTimeout = React.useCallback(async () => {
    try {
      await startCleanupRef.current(SESSION_EVENTS.LOGOUT, 'You have been logged out due to inactivity. Please sign in again to continue.');
    } catch (e) { console.log('Session timeout logout:', e); }
  }, []);
  useSessionTimeout(session.authUser, handleSessionTimeout, toast);

  // Toast state
  const [toasts, setToasts] = useState([]);
  const toastIdRef = React.useRef(0);

  React.useEffect(() => {
    registerToastHandler((message, type) => {
      const id = ++toastIdRef.current;
      setToasts(prev => [...prev, { id, message, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    });
    return () => { unregisterToastHandler(); };
  }, []);

  // Multi-tenancy state — profile and firm are LIVE (session gate listeners).
  // firmProfile is the live firm document; a local setFirmProfile (optimistic UI
  // after a subscription / branding change) only overrides it until the next
  // server snapshot of the firm arrives. Lifecycle decisions never read it.
  const liveFirm = isActiveSession ? session.firm : null;
  const liveFirmRef = useRef(liveFirm);
  liveFirmRef.current = liveFirm;
  const [firmOverride, setFirmOverride] = useState(null);
  const firmProfile = firmOverride && liveFirm && firmOverride.base === liveFirm ? firmOverride.value : liveFirm;
  const setFirmProfile = useCallback((value) => {
    setFirmOverride((prev) => {
      const base = liveFirmRef.current;
      const current = prev && prev.base === base ? prev.value : base;
      return { base, value: typeof value === 'function' ? value(current) : value };
    });
  }, []);
  // Pending invitations for this user — shown for an explicit choice only.
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesDismissed, setInvitesDismissed] = useState(false);
  // Platform super-admin (super-admin claim migration, 2026-10-06): the
  // superAdmin custom claim in the signed-in ID token is the ONLY source. The
  // Firestore profile's own superAdmin field means nothing any more and is
  // stripped; `platformAdmin` is a DERIVED, never-stored flag used only for the
  // UI entitlement (platform administrators get the full product experience).
  // Server rules / callables enforce authority from the token themselves.
  const [claimSuperAdmin, setClaimSuperAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setClaimSuperAdmin(false);
    if (!session.authUser) return undefined;
    getIdTokenResult(session.authUser).then((r) => { if (!cancelled) setClaimSuperAdmin(!!(r && r.claims && r.claims.superAdmin === true)); }).catch(() => {});
    return () => { cancelled = true; };
  }, [session.authUser]);
  const userProfile = useMemo(() => {
    if (!isActiveSession || !session.profile) return null;
    const { superAdmin: _profileFlagIgnored, ...rest } = session.profile;
    return { ...rest, platformAdmin: claimSuperAdmin };
  }, [isActiveSession, session.profile, claimSuperAdmin]);
  const [firmUsers, setFirmUsers] = useState([]);

  const userId = user?.uid;
  const firmId = user?.firmId;
  const userRole = user?.role || 'lawyer';

  // Role-based default permissions
  const ROLE_PERMISSIONS = {
    admin:     { canManageCases: true, canViewCases: true, canManageTasks: true, canManageClients: true, canManageBilling: true, canManageDocuments: true, canDeleteDocuments: true, canViewAnalytics: true, canManageAdmin: true, canInviteMembers: true, canChangeRoles: true, canUseAI: true, canUseResearch: true, canUseTemplates: true, canManagePortal: true },
    lawyer:    { canManageCases: true, canViewCases: true, canManageTasks: true, canManageClients: true, canManageBilling: true, canManageDocuments: true, canDeleteDocuments: true, canViewAnalytics: true, canManageAdmin: false, canInviteMembers: false, canChangeRoles: false, canUseAI: true, canUseResearch: true, canUseTemplates: true, canManagePortal: true },
    paralegal: { canManageCases: true, canViewCases: true, canManageTasks: true, canManageClients: true, canManageBilling: false, canManageDocuments: true, canDeleteDocuments: true, canViewAnalytics: false, canManageAdmin: false, canInviteMembers: false, canChangeRoles: false, canUseAI: true, canUseResearch: true, canUseTemplates: true, canManagePortal: false },
    secretary: { canManageCases: false, canViewCases: true, canManageTasks: true, canManageClients: true, canManageBilling: false, canManageDocuments: true, canDeleteDocuments: false, canViewAnalytics: false, canManageAdmin: false, canInviteMembers: false, canChangeRoles: false, canUseAI: true, canUseResearch: false, canUseTemplates: true, canManagePortal: false },
    intern:    { canManageCases: false, canViewCases: true, canManageTasks: false, canManageClients: false, canManageBilling: false, canManageDocuments: false, canDeleteDocuments: false, canViewAnalytics: false, canManageAdmin: false, canInviteMembers: false, canChangeRoles: false, canUseAI: true, canUseResearch: false, canUseTemplates: true, canManagePortal: false },
    agent:     { canManageCases: false, canViewCases: true, canManageTasks: true, canManageClients: true, canManageBilling: false, canManageDocuments: true, canDeleteDocuments: false, canViewAnalytics: false, canManageAdmin: false, canInviteMembers: false, canChangeRoles: false, canUseAI: true, canUseResearch: false, canUseTemplates: true, canManagePortal: false },
  };

  // Merge role defaults with any per-user overrides from Firestore
  // Role defaults take priority — only admin-set overrides (stored as _override suffix) can change them
  const roleDefaults = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.lawyer;
  const userPermissions = { ...roleDefaults };

  // Offline mode
  const { isOnline, queueCount, isSyncing, showBanner, sync, dismissBanner } = useOffline();

  // Real data from Firebase
  const [recentCases, setRecentCases] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Google Calendar state
  const [gcalConnected, setGcalConnected] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalInited, setGcalInited] = useState(false);

  // Initialize Google Calendar API
  useEffect(() => {
    initGoogleCalendar().then(r => {
      if (r.success) setGcalInited(true);
    });
  }, []);

  // Restore Google Calendar connection per-user
  useEffect(() => {
    if (gcalInited && userId) {
      setCurrentUserId(userId);
      const restored = restoreGoogleCalendarToken();
      if (restored) {
        setGcalConnected(true);
      } else {
        refreshGoogleCalendarToken().then(refreshed => {
          if (refreshed) setGcalConnected(true);
        });
      }
    }
  }, [gcalInited, userId]);

  // Stale tenant context (Hales Solicitors incident — recurrence prevention):
  // the server refused an upload because this session's firm no longer
  // matches the account's active firm. Reload so the profile, firm and every
  // firm-scoped view are re-derived — the server already failed the upload
  // closed, so nothing was written under the stale firm.
  useEffect(() => {
    const onStale = () => {
      toast('Your firm membership has changed — reloading FirmAssist.', 'warning');
      setTimeout(() => window.location.reload(), 1500);
    };
    window.addEventListener('firmassist:tenant-context-stale', onStale);
    return () => window.removeEventListener('firmassist:tenant-context-stale', onStale);
  }, []);

  // ── Session gate effects (session & auth-state handling, 2026-10-06) ──
  // Replaces the old one-time getUserProfile/getFirm auth listener. Profile and
  // firm are live; every non-ACTIVE state unmounts the shell (see render gates).

  // One cleanup for ALL firm-scoped React state held centrally here. Child
  // components (clients, documents, messages, team, billing…) hold their own
  // firm data and are unmounted whenever the shell is not rendered.
  const clearFirmState = useCallback(() => {
    setRecentCases([]);
    setUpcomingTasks([]);
    setMeetings([]);
    setFirmUsers([]);
    setFirmOverride(null);
    setPreselectedCase(null);
    setSearchQuery('');
    setActiveTab('dashboard');
    setAdminOnboardingDeepLink(null);
    setInvitesDismissed(false);
    setDataLoading(true);
    setDataVersion((v) => v + 1);
  }, []);

  // Clear on EVERY generation change (left ACTIVE, re-entered ACTIVE, tenant
  // X → Y). The shell only renders once the current generation has been cleared.
  const [clearedGeneration, setClearedGeneration] = useState(-1);
  useEffect(() => {
    clearFirmState();
    setClearedGeneration(session.generation);
  }, [session.generation, clearFirmState]);
  // ── MFA gate (MFA configuration & enrolment, 2026-10-06) ──
  // Decided from the live profile (role / superAdmin) and the Firebase user's
  // enrolled factors. While it blocks, the shell is not ready: no firm data is
  // loaded or listened to, and <MfaEnrollment> renders instead. It sits AFTER
  // the session gate's ACTIVE, so ACTIVE can no longer replace the prompt.
  const [mfaTick, setMfaTick] = useState(0);
  const mfaRequirementNow = isActiveSession && session.profile
    ? mfaRequirement({ uid: session.authUser && session.authUser.uid, role: session.profile.role, superAdmin: claimSuperAdmin })
    : MFA.NONE;
  const mfaEnrolled = useMemo(() => {
    try { return !!session.authUser && hasSecondFactor(multiFactor(session.authUser).enrolledFactors); } catch (e) { return false; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.authUser, mfaTick]);
  const mfaDeferKey = 'firmassist.mfaDeferred';
  const [mfaDeferredFor, setMfaDeferredFor] = useState(() => { try { return sessionStorage.getItem(mfaDeferKey); } catch (e) { return null; } });
  const mfaDeferred = !!session.tenant && mfaDeferredFor === tenantFingerprint(session.tenant);
  const mfaBlocking = isActiveSession && mfaGateBlocks({ requirement: mfaRequirementNow, enrolled: mfaEnrolled, deferred: mfaDeferred });
  const deferMfa = () => {
    const fp = tenantFingerprint(session.tenant);
    try { sessionStorage.setItem(mfaDeferKey, fp); } catch (e) { /* per-tab state still applies */ }
    setMfaDeferredFor(fp);
  };

  const shellReady = isActiveSession && clearedGeneration === session.generation && !mfaBlocking;

  // Canonical device cleanup: LOGOUT / TENANT_CHANGED / FIRM_ACCESS_LOST.
  const cleanupPhaseRef = useRef(null);
  const startCleanup = useCallback(async (kind, recoveryMessage) => {
    if (cleanupPhaseRef.current) return;
    cleanupPhaseRef.current = 'running';
    setCleanupPhase('running');
    clearFirmState();
    if (kind === SESSION_EVENTS.LOGOUT) { session.skipAuthGrace(); trackLogout(); }
    if (recoveryMessage) stashRecoveryError(recoveryMessage);
    const result = await runSessionCleanup(kind, { uid: sessionUidRef.current, deps: sessionCleanupDeps });
    if (result.persistence && !result.persistence.cleared) console.warn('[Session] Local Firestore cache could not be cleared now (' + result.persistence.reason + ') — retried at next start.');
    if (!result.reloaded) { cleanupPhaseRef.current = 'reload-required'; setCleanupPhase('reload-required'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFirmState]);
  startCleanupRef.current = startCleanup;

  // Decide device cleanup from the gate's (server-trusted) outcome. The device
  // marker is removed before the cleanup reload, so this can never loop.
  useEffect(() => {
    if (cleanupPhaseRef.current) return;
    if (sessionStatus === SESSION.SIGNED_OUT && session.reason === 'SESSION_ENDED') {
      // Phase B: this session's sign-in predates the account's security epoch
      // (or the epoch is malformed) — end it everywhere on this device with the
      // canonical cleanup (state, caches, persistence, other tabs, local
      // sign-out) and the neutral notice. The reason is never shown.
      startCleanup(SESSION_EVENTS.LOGOUT, SESSION_ENDED_NOTICE);
      return;
    }
    if (sessionStatus === SESSION.SIGNED_OUT && session.reason === 'PROFILE_MISSING') {
      // P0-4: an authenticated user with no Firestore profile at all (and no
      // registration in flight) — sign out to a clean, recoverable Login screen.
      console.error('[Auth] No profile found for authenticated user — signing out to avoid a broken app shell.');
      startCleanup(SESSION_EVENTS.LOGOUT, "We couldn't finish setting up your account. Please try registering again, or contact support if this keeps happening.");
      return;
    }
    const deviceTenant = getDeviceTenant();
    if (sessionStatus === SESSION.SIGNED_OUT && deviceTenant) {
      // Firebase Auth ended while this device still held firm data. A deliberate
      // local sign-out (logoutUser marks it) gets no message; anything else —
      // e.g. sessions revoked / account disabled elsewhere, detected at token
      // refresh or reload — gets the neutral notice (never the reason).
      let local = false;
      try { local = sessionStorage.getItem(LOCAL_SIGNOUT_FLAG) === '1'; sessionStorage.removeItem(LOCAL_SIGNOUT_FLAG); } catch (e) { /* ignore */ }
      startCleanup(SESSION_EVENTS.LOGOUT, local ? undefined : SESSION_ENDED_NOTICE);
      return;
    }
    if (sessionStatus !== SESSION.SIGNED_OUT && sessionStatus !== SESSION.AUTH_LOADING) {
      try { sessionStorage.removeItem(LOCAL_SIGNOUT_FLAG); } catch (e) { /* ignore */ }
    }
    const kind = sessionStatus === SESSION.SIGNED_OUT ? null : cleanupNeeded({ status: sessionStatus, tenant: session.tenant, deviceTenant });
    if (kind) { startCleanup(kind); return; }
    if (sessionStatus === SESSION.ACTIVE && session.tenant) setDeviceTenant(session.tenant);
    // Incident 2026-10-21: a post-success registration hold ends once ACTIVE.
    if (sessionStatus === SESSION.ACTIVE) releaseRegistrationHold();
  }, [sessionStatus, session.tenant, session.reason, startCleanup]);

  // "Sign out of all devices" (Phase A): the server has revoked every refresh
  // token; finish with the canonical logout (state, caches, other tabs).
  useEffect(() => {
    const onSignedOutAll = () => { startCleanup(SESSION_EVENTS.LOGOUT, "You've been signed out of all devices. Please sign in again."); };
    window.addEventListener(SIGNED_OUT_ALL_EVENT, onSignedOutAll);
    return () => window.removeEventListener(SIGNED_OUT_ALL_EVENT, onSignedOutAll);
  }, [startCleanup]);

  // Other tabs: an access change there means this tab's firm data is no longer
  // current (and its Firestore cache is being cleared) — leave ACTIVE now.
  useEffect(() => subscribeSessionEvents(() => {
    if (cleanupPhaseRef.current) return;
    const st = sessionStatusRef.current;
    if (st === SESSION.SIGNED_OUT || st === SESSION.AUTH_LOADING) return;
    cleanupPhaseRef.current = 'running';
    setCleanupPhase('running');
    clearFirmState();
    (async () => {
      try { await terminate(db); } catch (e) { /* already terminated */ }
      setTimeout(() => { if (!guardedReload()) { cleanupPhaseRef.current = 'reload-required'; setCleanupPhase('reload-required'); } }, 300);
    })();
  }), [clearFirmState]);

  // Pending invitations (never accepted automatically — explicit choice only,
  // re-checked by the server). Listed for every signed-in non-loading state.
  const inviteScope = session.authUser && [SESSION.ACTIVE, SESSION.NO_FIRM, SESSION.SUSPENDED, SESSION.FIRM_UNAVAILABLE].includes(sessionStatus) ? `${session.authUser.uid}|${sessionStatus}` : null;
  useEffect(() => {
    if (!inviteScope) { setPendingInvites([]); return; }
    // M1: an unverified address gets only a count — shown as one locked entry
    // asking the person to verify before any invitation detail is revealed.
    listMyPendingInvites().then((r) => {
      if (!r.success) return;
      setPendingInvites(r.emailVerificationRequired && r.pendingCount > 0 ? [lockedInvitesPlaceholder(r.pendingCount)] : r.invites);
    }).catch(() => {});
  }, [inviteScope]);

  // Per-tenant session start: analytics identity + legacy subscription migration.
  useEffect(() => {
    if (!session.tenant || !user) return;
    // ── ANALYTICS: Identify user ──
    identifyUser(user.uid, { firmId: user.firmId, role: user.role, plan: user.userProfile.plan || 'solo', firmName: '' });
    trackSessionStart(window.innerWidth < 768 ? 'mobile' : 'desktop');
    // Session 16: migrate legacy string subscription to object shape (idempotent,
    // non-blocking). The live firm listener picks up the result.
    if (session.firm && !(session.firm.subscription && typeof session.firm.subscription === 'object')) {
      migrateLegacySubscription(session.firm).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.tenant]);

  // Load cases and tasks from Firebase — once per ACTIVE generation (a live
  // profile snapshot no longer re-triggers the whole load).
  useEffect(() => {
    if (shellReady && user) {
      loadData();
      // ── FCM PUSH NOTIFICATIONS ──
      // Request permission and register for push notifications
      (async () => {
        try {
          if (!('Notification' in window)) return;
          
          let permission = Notification.permission;
          if (permission === 'default') {
            permission = await Notification.requestPermission();
          }
          if (permission !== 'granted') return;

          // Register FCM service worker
          const fcmReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          console.log('[FCM] Service worker registered');

          // Get FCM token
          const { getApp } = await import('firebase/app');
          const app = getApp();
          const messaging = getMessaging(app);

          // VAPID key — generate at Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
          // Replace with your actual VAPID key
          const VAPID_KEY = (firmProfile?.fcmVapidKey) || 'BEUik3lgPtGeMpm-fKXd6CxKl_ovUSnywftIWqExQE9WgZKGkkHQp1LUdTmnA3yK2WrvKfMtY38Qjod_1aSYgo0';
          if (!VAPID_KEY) {
            console.log('[FCM] No VAPID key configured — skipping token registration. Set fcmVapidKey in firmProfile or Firebase Console.');
            return;
          }

          const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: fcmReg });
          if (token) {
            console.log('[FCM] Token obtained');
            // Store token in Firestore for this user
            try {
              const { setDoc } = await import('firebase/firestore');
              await setDoc(doc(db, 'fcmTokens', user.uid), {
                token,
                userId: user.uid,
                firmId: user.firmId || '',
                email: user.email || '',
                name: user.displayName || '',
                updatedAt: new Date().toISOString(),
                platform: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
                userAgent: navigator.userAgent.substring(0, 200)
              }, { merge: true });
            } catch (e) { console.log('[FCM] Token storage error:', e.message); }
          }

          // Handle foreground messages — show toast notification
          onMessage(messaging, (payload) => {
            console.log('[FCM] Foreground message:', payload);
            const title = payload.notification?.title || payload.data?.title || 'FirmAssist';
            const body = payload.notification?.body || payload.data?.body || '';
            const type = payload.data?.type || 'general';
            
            // Show in-app toast
            toast(title + (body ? ': ' + body : ''), type === 'court_reminder' ? 'error' : 'info');
            
            // Also show browser notification for visibility
            if (document.hidden) {
              new Notification(title, { body, icon: '/icons/icon-192.png', tag: 'firmassist-fg-' + Date.now() });
            }
          });
        } catch (e) { console.log('[FCM] Setup error:', e.message); }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shellReady, session.generation]);

  // Generation guard (session/loadFirmData.js): a load started for firm X /
  // generation N never updates state (or the offline cache) once the session
  // has left ACTIVE or moved to another generation / tenant. Offline entries
  // are keyed by uid + firmId.
  const loadData = async (showLoading = true) => {
    if (!user || !firmId) return;
    const gen = sessionGenRef.current;
    if (showLoading) setDataLoading(true);
    const { cacheData, getCachedData } = await import('./hooks/useOffline');
    await loadFirmData({
      uid: user.uid,
      firmId,
      online: navigator.onLine,
      isCurrent: () => isActiveRef.current && sessionGenRef.current === gen,
      services: { getCases, getEnhancedTasks, getMeetings, getFirmUsers },
      cache: { key: offlineCacheKey, get: getCachedData, put: cacheData },
      set: { cases: setRecentCases, tasks: setUpcomingTasks, meetings: setMeetings, firmUsers: setFirmUsers, loading: setDataLoading },
    });
  };

  // Real-time listener for tasks — updates automatically when any team member changes data
  // A permission-denied firm-scoped listener means this data is no longer
  // authorised: drop it and ask the session gate to re-confirm profile + firm
  // from the server (it leaves ACTIVE meanwhile). Other errors (network) are
  // not treated as authorization loss.
  const { reportAccessLost } = session;
  useEffect(() => {
    if (!shellReady || !userId || !firmId) return;
    const q = query(collection(db, 'tasks'), where('firmId', '==', firmId));
    const unsubTasks = onSnapshot(q, (snapshot) => {
      const tasks = [];
      snapshot.forEach(d => tasks.push({ id: d.id, ...d.data() }));
      tasks.sort((a, b) => compareDueDatesForSort(a.dueDate, b.dueDate));
      setUpcomingTasks(tasks);
    }, (err) => {
      console.log('Tasks listener error:', err && err.code);
      if (isPermissionDeniedError(err)) { setUpcomingTasks([]); reportAccessLost(); }
    });
    return () => unsubTasks();
  }, [shellReady, userId, firmId, session.generation, reportAccessLost]);

  // Real-time listener for cases
  useEffect(() => {
    if (!shellReady || !userId || !firmId) return;
    const q = query(collection(db, 'cases'), where('firmId', '==', firmId));
    const unsubCases = onSnapshot(q, (snapshot) => {
      const cases = [];
      snapshot.forEach(d => cases.push({ id: d.id, ...d.data() }));
      cases.sort((a, b) => {
        const aTime = a.createdAt?.seconds || a.createdAt?.getTime?.() || 0;
        const bTime = b.createdAt?.seconds || b.createdAt?.getTime?.() || 0;
        return bTime - aTime;
      });
      setRecentCases(cases);
    }, (err) => {
      console.error('[Cases listener] Firestore error:', err && err.code);
      if (isPermissionDeniedError(err)) { setRecentCases([]); reportAccessLost(); }
    });
    return () => unsubCases();
  }, [shellReady, userId, firmId, session.generation, reportAccessLost]);
  // Firm lifecycle is now decided live by the session gate (firm listener):
  // archived / deleted / missing / unknown-status → FIRM_UNAVAILABLE without a
  // reload. A transient read failure is ERROR_RETRYABLE, never "unavailable".

  // Handle logout — the one canonical logout flow (see sessionCleanup.js).
  const handleLogout = async () => {
    await startCleanup(SESSION_EVENTS.LOGOUT);
  };

  // Handle return from Paystack hosted checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trxref = urlParams.get('trxref') || urlParams.get('reference');
    const pending = sessionStorage.getItem('paystackPending');
    
    if (trxref && pending) {
      sessionStorage.removeItem('paystackPending');
      window.history.replaceState({}, document.title, window.location.pathname);
      
      const { firmId: pendingFirmId } = JSON.parse(pending);

      (async () => {
        try {
          // M3: the server verifies against its own payment intent; the plan
          // shown is the one it applied, never the browser's.
          const verifyFn = httpsCallable(functions, 'verifyPaystackPayment');
          const result = await verifyFn({ reference: trxref, firmId: pendingFirmId || firmId });
          if (result.data.success) {
            const plan = result.data.plan;
            toast('Subscription activated! Welcome to FirmAssist ' + (PLANS[plan]?.name || plan) + '.', 'success');
            const firmResult = await getFirm(firmId);
            if (firmResult.success) setFirmProfile(firmResult.firm);
          } else {
            toast('Payment verification failed. Ref: ' + trxref, 'error');
          }
        } catch (e) {
          toast('Verification error: ' + e.message, 'error');
        }
      })();
    }
  }, []);

// Client Portal Route
  const portalPath = window.location.pathname;
  if (portalPath.startsWith('/portal/')) {
    const portalLinkId = portalPath.split('/portal/')[1];
    if (portalLinkId) return <Suspense fallback={<TabLoader />}><ClientStatusPage linkId={portalLinkId} /></Suspense>;
  }
  
  // ── SESSION GATES (session & auth-state handling, 2026-10-06) ──────────────
  // The application shell renders ONLY in ACTIVE (see session/sessionModel.js).
  // Device cleanup in progress (logout / firm change / access lost).
  if (cleanupPhase) return <SessionBusy reloadRequired={cleanupPhase === 'reload-required'} />;
  // Login stays mounted while its own registration / bootstrap flow is in
  // flight (the profile does not exist yet — never treated as removed/missing).
  // A profile-less session that is signing out (gate reason SIGNING_OUT) also
  // keeps Login mounted, so its notice (e.g. "verify your email") is not lost.
  const showLogin = sessionStatus === SESSION.SIGNED_OUT || (session.registering && sessionStatus !== SESSION.ACTIVE) || session.reason === 'SIGNING_OUT';
  const loading = !showLogin && (sessionStatus === SESSION.AUTH_LOADING || sessionStatus === SESSION.PROFILE_LOADING
    || sessionStatus === SESSION.FIRM_LOADING || (isActiveSession && clearedGeneration !== session.generation));

    // Show loading screen
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#080a10',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif"
      }}>
        <style>{`
          @keyframes firmassist-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
          @keyframes firmassist-bar { 0% { width: 0%; } 100% { width: 100%; } }
          @keyframes firmassist-fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', animation: 'firmassist-fadeIn 0.6s ease-out' }}>
          <Scale size={44} style={{ color: '#f59e0b', animation: 'firmassist-pulse 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '4px', color: '#e8eaed' }}>FIRMASSIST</span>
        </div>
        <div style={{ width: '220px', height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', marginBottom: '1.25rem' }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, #f59e0b, #d4a853)', borderRadius: '4px', animation: 'firmassist-bar 2s ease-in-out infinite' }} />
        </div>
        <div style={{ color: '#8b8fa3', fontSize: '0.9rem', animation: 'firmassist-fadeIn 0.8s ease-out 0.2s both', fontStyle: 'italic' }}>{(() => {
          const maxims = [
            'Lex est quod notamus — The law is what we record',
            'Justitia omnibus — Justice for all',
            'Fiat justitia ruat caelum — Let justice be done though the heavens fall',
            'Audi alteram partem — Hear the other side',
            'Nemo judex in causa sua — No one shall be judge in their own cause',
            'Ubi jus ibi remedium — Where there is a right, there is a remedy',
            'Salus populi suprema lex — The welfare of the people is the supreme law',
            'Ignorantia juris non excusat — Ignorance of the law is no excuse',
            'Rex non potest peccare — The law is above all',
            'Actus non facit reum nisi mens sit rea — The act is not culpable unless the mind is guilty',
            'Ei incumbit probatio qui dicit — The burden of proof lies upon him who affirms',
            'Res judicata pro veritate accipitur — A matter adjudged is accepted as truth',
            'Dura lex sed lex — The law is harsh, but it is the law',
            'In dubio pro reo — When in doubt, for the accused',
            'Pacta sunt servanda — Agreements must be kept',
            'Jus est ars boni et aequi — Law is the art of the good and the just',
            'Nulla poena sine lege — No penalty without a law',
            'Sic utere tuo ut alienum non laedas — Use your own so as not to harm another',
            'Volenti non fit injuria — No injury is done to a willing person',
            'Lex prospicit non respicit — The law looks forward, not backward',
            'Cessante ratione legis cessat lex — When the reason for the law ceases, the law itself ceases',
            'Delegatus non potest delegare — A delegate cannot delegate',
            'Expressio unius est exclusio alterius — The expression of one thing is the exclusion of another',
            'Nemo dat quod non habet — No one gives what they do not have',
            'Veritas nihil veretur nisi abscondi — Truth fears nothing but to be hidden',
            'De minimis non curat lex — The law does not concern itself with trifles',
            'Qui facit per alium facit per se — He who acts through another acts himself',
            'Bona fide — In good faith',
            'Lex non cogit ad impossibilia — The law does not compel the impossible',
            'Nullum crimen sine lege — No crime without law',
            'Ex turpi causa non oritur actio — No action arises from a wrongful cause',
            'Ratio decidendi — The reason for the decision',
            'Stare decisis — Stand by what has been decided',
            'Caveat emptor — Let the buyer beware',
            'Mala fides — Bad faith must always be proven',
            'Inter arma enim silent leges — In times of war, the law falls silent',
            'Justitia est constans et perpetua voluntas — Justice is the constant and perpetual will',
            'Lex semper dabit remedium — The law will always give a remedy',
            'Quod ab initio non valet, in tractu temporis non convalescit — What is not valid from the beginning cannot be made valid by time',
            'Actori incumbit onus probandi — The claimant bears the burden of proof',
            'Nemo debet bis vexari pro una et eadem causa — No one ought to be twice troubled for the same cause',
            'Non est factum — It is not my deed',
            'Ultra vires — Beyond one\'s powers',
            'Obiter dictum — A remark made in passing',
            'Amicus curiae — Friend of the court',
            'Prima facie — On first appearance',
            'Habeas corpus — You shall have the body',
            'Mens rea — Guilty mind',
            'Actus reus — Guilty act',
            'Sub judice — Under judicial consideration',
            'Sine qua non — Without which, not',
            'Locus standi — The right to bring an action'
          ];
          const weekNumber = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
          return maxims[weekNumber % maxims.length];
        })()}</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (showLogin) {
    // In the return where Login is rendered:
  return (<div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #080a10 0%, #0d1017 50%, #080a10 100%)' }}>
    <Login initialError={authRecoveryError} onDeliberateSignOut={session.skipAuthGrace} onLoginSuccess={() => {
    // The session gate is authoritative: it reaches ACTIVE (or a no-access
    // state) from the live profile / firm listeners, not from Login's copy.
    setAuthRecoveryError('');
    // Firm-move lifecycle (2026-10-05): no invitation is ever marked accepted
    // here — acceptance happens only through the acceptFirmInvite callable
    // after an explicit choice (<PendingInvitations>).
  }} />
    {/* Security Trust Badges */}
    <div style={{ padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', background: 'transparent' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
        {[
          { icon: '🔒', label: '256-bit SSL Encryption' },
          { icon: '🛡️', label: 'Firebase App Check' },
          { icon: '🇬🇭', label: 'Ghana DPA (Act 843)' },
          { icon: '☁️', label: 'Google Cloud Secured' },
        ].map((badge, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.7rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', color: '#8b8fa3', fontSize: '0.72rem' }}>
            <span>{badge.icon}</span> {badge.label}
          </div>
        ))}
      </div>
      <div style={{ color: '#4b5563', fontSize: '0.68rem', textAlign: 'center', maxWidth: '400px', lineHeight: 1.5 }}>
        Your data is encrypted in transit and at rest. FirmAssist complies with the Ghana Data Protection Act, 2012 (Act 843) and legal professional privilege requirements.
      </div>
      <div style={{ color: '#4b5563', fontSize: '0.68rem', textAlign: 'center', maxWidth: '400px', lineHeight: 1.5 }}>
        FirmAssist is a product of Tomken Technologies Limited Company (Company Registration No. CS089200421).
      </div>
      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.7rem' }}>
        <a href="/privacy" style={{ color: '#d4a853', textDecoration: 'none' }}>Privacy Policy</a>
        <span style={{ color: '#4b5563' }}>•</span>
        <a href="/terms" style={{ color: '#d4a853', textDecoration: 'none' }}>Terms of Service</a>
        <span style={{ color: '#4b5563' }}>•</span>
        <a href="https://tomkentechnologies.com" target="_blank" rel="noopener noreferrer" style={{ color: '#d4a853', textDecoration: 'none' }}>TomKen Technologies</a>
      </div>
    </div>
  </div>);
}

  // Transient failure — never treated as removal / suspension / firm loss.
  if (sessionStatus === SESSION.ERROR_RETRYABLE) {
    return <SessionRetry onRetry={session.retry} onSignOut={handleLogout} />;
  }
  const accountName = (session.profile && session.profile.displayName) || (session.authUser && session.authUser.displayName) || '';
  if (sessionStatus === SESSION.SUSPENDED) {
    return (
      <SuspendedAccess
        inactive={session.reason === 'INACTIVE'}
        invites={pendingInvites}
        currentFirmId={(session.profile && session.profile.firmId) || null}
        displayName={accountName}
        onSignOut={handleLogout}
      />
    );
  }
  if (sessionStatus === SESSION.NO_FIRM) {
    return <NoFirmAccess invites={pendingInvites} displayName={accountName} onSignOut={handleLogout} />;
  }
  if (sessionStatus === SESSION.FIRM_UNAVAILABLE) {
    return (
      <FirmUnavailable
        invites={pendingInvites}
        currentFirmId={(session.profile && session.profile.firmId) || null}
        displayName={accountName}
        onSignOut={handleLogout}
      />
    );
  }
  // Defensive: nothing below may render outside a cleared ACTIVE generation.
  // Second factor required / recommended before the shell (see mfaPolicy.js).
  if (mfaBlocking) {
    return (
      <MfaEnrollment
        user={session.authUser}
        requirement={mfaRequirementNow}
        onEnrolled={() => setMfaTick((t) => t + 1)}
        onDefer={deferMfa}
        onSignOut={handleLogout}
      />
    );
  }
  if (!shellReady || !user) return <SessionBusy />;

  // Show loading while fetching data
  if (dataLoading && recentCases.length === 0 && upcomingTasks.length === 0) {
    const loadingMaxims = [
      { latin: 'Fiat justitia ruat caelum', english: 'Let justice be done though the heavens fall' },
      { latin: 'Audi alteram partem', english: 'Hear the other side' },
      { latin: 'Nemo judex in causa sua', english: 'No one should be a judge in their own cause' },
      { latin: 'Ubi jus ibi remedium', english: 'Where there is a right, there is a remedy' },
      { latin: 'Ignorantia juris non excusat', english: 'Ignorance of the law is no excuse' },
      { latin: 'Salus populi suprema lex esto', english: 'The welfare of the people shall be the supreme law' },
      { latin: 'Justitia omnibus', english: 'Justice for all' },
      { latin: 'Veritas vincit', english: 'Truth conquers' },
      { latin: 'Dura lex sed lex', english: 'The law is harsh, but it is the law' },
      { latin: 'Rex non potest peccare', english: 'The king can do no wrong' },
      { latin: 'Actus reus non facit reum nisi mens sit rea', english: 'An act does not make one guilty unless the mind is guilty' },
      { latin: 'Ei incumbit probatio qui dicit', english: 'The burden of proof lies upon the one who affirms' },
    ];
    const maxim = loadingMaxims[Math.floor(Math.random() * loadingMaxims.length)];
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #080a10 0%, #0d1017 50%, #080a10 100%)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'DM Sans', -apple-system, sans-serif",
        padding: '2rem'
      }}>
        {/* Logo */}
        <div style={{ width: '52px', height: '52px', background: 'linear-gradient(135deg, #d4a853, #b8862d)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', fontSize: '1.5rem' }}>⚖️</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#e8eaed', marginBottom: '0.3rem' }}>FirmAssist</div>
        <div style={{ color: '#8b8fa3', fontSize: '0.75rem', fontWeight: 500, marginBottom: '2.5rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Legal Practice Manager</div>

        {/* Animated dots loader */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2.5rem' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #d4a853, #b8862d)',
              animation: `loadPulse 1.4s ease-in-out ${i * 0.2}s infinite`
            }} />
          ))}
        </div>

        {/* Latin maxim */}
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: '#d4a853', fontSize: '1.15rem', fontWeight: 600,
            fontStyle: 'italic', marginBottom: '0.5rem', lineHeight: 1.4
          }}>
            "{maxim.latin}"
          </div>
          <div style={{ color: '#8b8fa3', fontSize: '0.85rem' }}>
            — {maxim.english}
          </div>
        </div>

        {/* Loading text */}
        <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: '2rem', letterSpacing: '0.05em' }}>
          Preparing your workspace...
        </div>

        <style>{`
          @keyframes loadPulse {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // ============================================================================
  // SUBSCRIPTION GATE — full lockout if expired
  // ============================================================================
  const subStatus = checkSubscription(firmProfile, userProfile);
  const planFeatures = getPlanFeatures(firmProfile, userProfile);

  // ============================================================================
  // SESSION 16 — TRIAL TIER SELECTION GATE (must run BEFORE Paywall gate)
  // ----------------------------------------------------------------------------
  // Firms with subscription.status === 'pending_selection' must choose a tier
  // before they can use the app. Runs above the Paywall gate so new firms
  // never get misread as expired. SuperAdmin bypasses.
  // ============================================================================
  if (
    !userProfile?.platformAdmin &&
    firmProfile?.subscription &&
    typeof firmProfile.subscription === 'object' &&
    firmProfile.subscription.status === 'pending_selection' &&
    !subStatus.isExpired // Phase 2: after the trial end the firm converts through checkout, not the free tier choice
  ) {
    return (
      <TrialTierSelectionModal
        firmId={firmId}
        firmProfile={firmProfile}
        onSelected={(updatedFirm) => setFirmProfile(updatedFirm)}
      />
    );
  }

  // Phase 2 (2026-10-12): an expired subscription NO LONGER locks the firm
  // out. Records and essential practice management stay available; paid AI /
  // automation services pause server-side (functions/subscriptionEntitlementGate.js)
  // and the ExpiredSubscriptionBanner below offers the renewal path.

  // Static data (will be replaced with real data later)
  // eslint-disable-next-line no-unused-vars
  const practiceAreaStats = [
    { area: 'Criminal Defense', cases: 15, percentage: 30, revenue: '$145,000' },
    { area: 'Corporate Law', cases: 12, percentage: 24, revenue: '$230,000' },
    { area: 'Estate Planning', cases: 10, percentage: 20, revenue: '$85,000' },
    { area: 'Labor Law', cases: 8, percentage: 16, revenue: '$95,000' },
    { area: 'Family Law', cases: 5, percentage: 10, revenue: '$65,000' },
  ];

  // eslint-disable-next-line no-unused-vars
  const recentDocuments = [
    { id: 1, name: 'Motion for Summary Judgment.pdf', case: 'Johnson v. State', uploaded: '2026-02-06', size: '2.4 MB' },
    { id: 2, name: 'Merger Agreement Draft.docx', case: 'Tech Corp Merger', uploaded: '2026-02-05', size: '1.8 MB' },
    { id: 3, name: 'Last Will and Testament.pdf', case: 'Estate of Williams', uploaded: '2026-02-04', size: '890 KB' },
  ];


  // ONB-08/ONB-09: the checklist's "Set Up" (reminders) and "Connect"
  // (interactive WhatsApp) CTAs both land on the authenticated user's own
  // personal notification settings — never a generic "open Admin Panel".
  // Canonical location differs by role only in WHICH existing AdminPanel tab
  // already renders those settings (per AdminPanel.js's own tab list):
  // admin -> 'notifications' (WhatsApp Notifications), everyone else ->
  // 'account' (Account, allRoles: true). No new settings UI is created.
  const navigateToPersonalNotificationSettings = (section) => {
    const tab = userRole === 'admin' ? 'notifications' : 'account';
    const anchorId = section === 'whatsapp' ? 'onboarding-connect-whatsapp' : null;
    setAdminOnboardingDeepLink({ tab, section: anchorId });
    setActiveTab('admin');
  };

  const renderContent = () => {
      switch (activeTab) {
      case 'dashboard':
        return <Dashboard
          recentCases={recentCases}
          upcomingTasks={upcomingTasks}
          userId={user.uid}
          firmId={firmId}
          setActiveTab={setActiveTab}
          user={user}
          onCaseCreated={() => { loadData(false); triggerDataRefresh(); }}
          dataVersion={dataVersion}
          userRole={userRole}
          userPermissions={userPermissions}
          planFeatures={planFeatures}
          activeTab={activeTab}
          onNavigateToPersonalNotificationSettings={navigateToPersonalNotificationSettings}
        />;
      case 'ai-assistant':
        return null; // Rendered in kept-alive section
      case 'cases':
        return (
          <CaseManagement
            recentCases={recentCases}
            upcomingTasks={upcomingTasks}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onCaseCreated={() => { loadData(false); triggerDataRefresh(); }}
            userId={user.uid}
            firmId={firmId}
            userRole={userRole}
            userPermissions={userPermissions}
            setActiveTab={setActiveTab}
            setPreselectedCase={setPreselectedCase}
            user={user}
            firmUsers={firmUsers}
            firmProfile={firmProfile}
            userProfile={userProfile}
            CasePlaybookTracker={CasePlaybookTracker}
          />
        );
      case 'documents':
        return <DocumentManagement userId={user.uid} firmId={firmId} cases={recentCases} userPermissions={userPermissions} firmProfile={firmProfile} userProfile={userProfile} />;
      case 'tasks':
        return <TaskCalendar cases={recentCases} userId={userId} firmId={firmId} preselectedCase={preselectedCase} setPreselectedCase={setPreselectedCase} gcalConnected={gcalConnected} gcalInited={gcalInited} setGcalConnected={setGcalConnected} meetings={meetings} upcomingTasks={upcomingTasks} firmProfile={firmProfile} userProfile={userProfile} user={user} />;
      case 'clients':
        return <ClientManagement userId={user.uid} firmId={firmId} cases={recentCases} userRole={userRole} firmProfile={firmProfile} userProfile={userProfile} dataVersion={dataVersion} />;
      case 'billing':
        if (!userPermissions.canManageBilling) return <div style={{ padding: '4rem', textAlign: 'center' }}><Shield size={64} style={{ color: '#ef4444', opacity: 0.3, margin: '0 auto 1rem', display: 'block' }} /><h2 style={{ color: '#e8eaed', margin: '0 0 0.5rem' }}>Access Restricted</h2><p style={{ color: '#8b8fa3' }}>Your role does not have access to Billing & Time. Contact your firm admin.</p></div>;
        return <Billing userId={user.uid} firmId={firmId} cases={recentCases} user={user} firmProfile={firmProfile} userProfile={userProfile} dataVersion={dataVersion} />;
      case 'legal-research':
        return <LegalResearch userId={user.uid} firmId={firmId} currentUser={user} firmProfile={firmProfile} />;
      case 'audit':
        if (userRole !== 'admin') return <div style={{ padding: '4rem', textAlign: 'center' }}><Shield size={64} style={{ color: '#ef4444', opacity: 0.3, margin: '0 auto 1rem', display: 'block' }} /><h2 style={{ color: '#e8eaed', margin: '0 0 0.5rem' }}>Admin Only</h2><p style={{ color: '#8b8fa3' }}>Only administrators can access the audit trail.</p></div>;
        return <AuditTrailView userId={user.uid} firmId={firmId} />;
      case 'analytics': {
        const _pf = getPlanFeatures(firmProfile, userProfile);
        if (!_pf.analytics) return <UpgradePrompt feature="Analytics Dashboard" requiredPlan="Firm" setActiveTab={setActiveTab} />;
        if (!userPermissions.canViewAnalytics) return <div style={{ padding: '4rem', textAlign: 'center' }}><Shield size={64} style={{ color: '#ef4444', opacity: 0.3, margin: '0 auto 1rem', display: 'block' }} /><h2 style={{ color: '#e8eaed', margin: '0 0 0.5rem' }}>Access Restricted</h2><p style={{ color: '#8b8fa3' }}>Your role does not have access to Analytics. Contact your firm admin.</p></div>;
        return <><Analytics userId={user.uid} firmId={firmId} firmProfile={firmProfile} dataVersion={dataVersion} /><ReportsExport userId={user.uid} firmId={firmId} cases={recentCases} firmProfile={firmProfile} /></>;
      }
      case 'team': {
        const _pf = getPlanFeatures(firmProfile, userProfile);
        if (!_pf.teamHub) return <UpgradePrompt feature="Team Hub" requiredPlan="Firm" setActiveTab={setActiveTab} />;
        return <TeamHub userId={user.uid} firmId={firmId} cases={recentCases} userRole={userRole} user={user} firmProfile={firmProfile} dataVersion={dataVersion} />;
      }
      case 'admin':
        return <AdminPanel userId={user.uid} firmId={firmId} firmProfile={firmProfile} setFirmProfile={setFirmProfile} firmUsers={firmUsers} setFirmUsers={setFirmUsers} userRole={userRole} gcalConnected={gcalConnected} gcalInited={gcalInited} setGcalConnected={setGcalConnected} planFeatures={planFeatures} onTeamUpdate={() => setDataVersion(v => v + 1)} currentUser={user} SubscriptionManager={SubscriptionManager} initialAdminTab={adminOnboardingDeepLink?.tab} initialAdminSection={adminOnboardingDeepLink?.section} onInitialAdminTabConsumed={() => setAdminOnboardingDeepLink(null)} />;
      case 'templates': {
        const _pf = getPlanFeatures(firmProfile, userProfile);
        if (!_pf.templates) return <UpgradePrompt feature="Document Templates" requiredPlan="Firm" setActiveTab={setActiveTab} />;
        return <DocumentTemplates setActiveTab={setActiveTab} userId={userId} firmId={firmId} cases={recentCases} user={user} firmProfile={firmProfile} userProfile={userProfile} />;
      }
        case 'conflict':
        return <ConflictCheck />;
      case 'borrow':
        return <BorrowRequests userId={user.uid} firmId={firmId} userRole={userRole} user={user} firmProfile={firmProfile} />;
      case 'contact':
        return <ContactUs />;
      default:
        return <Dashboard
          recentCases={recentCases}
          upcomingTasks={upcomingTasks}
          userId={user.uid}
          firmId={firmId}
          setActiveTab={setActiveTab}
          user={user}
          onCaseCreated={() => { loadData(false); triggerDataRefresh(); }}
          dataVersion={dataVersion}
          userRole={userRole}
          userPermissions={userPermissions}
          planFeatures={planFeatures}
          activeTab={activeTab}
          onNavigateToPersonalNotificationSettings={navigateToPersonalNotificationSettings}
        />;
    }
  };

  return (
    <SubscriptionContext.Provider value={{ subStatus, planFeatures, firmProfile, userProfile, setFirmProfile }}>
    <>
      <OfflineBanner
        isOnline={isOnline}
        queueCount={queueCount}
        isSyncing={isSyncing}
        showBanner={showBanner}
        onSync={sync}
        onDismiss={dismissBanner}
      />
      <ToastContainer toasts={toasts} onRemove={(id) => setToasts(prev => prev.filter(t => t.id !== id))} />
      {/* Trial countdown banner */}
      {subStatus.isTrialing && subStatus.daysLeft <= 7 && (
        <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))', borderBottom: '1px solid rgba(245,158,11,0.3)', padding: '0.6rem 2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', fontSize: '0.85rem' }}>
          <span style={{ color: '#f59e0b' }}>⏰ {subStatus.daysLeft} day{subStatus.daysLeft !== 1 ? 's' : ''} left on your free trial</span>
          {userRole === 'admin' && <button onClick={() => {
            // Force paywall
            setFirmProfile(prev => ({ ...prev, subscription: { status: 'expired', expiresAt: new Date(0) } }));
          }} style={{ background: 'linear-gradient(135deg, #d4a853, #e8c875)', color: '#080a10', border: 'none', borderRadius: '6px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit' }}>Upgrade Now</button>}
        </div>
      )}
      <div className="firmassist">
      <Header 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        notifications={notifications}
        user={user}
        onLogout={handleLogout}
        firmProfile={firmProfile}
        setActiveTab={setActiveTab}
        recentCases={recentCases}
        upcomingTasks={upcomingTasks}
        meetings={meetings}
        userRole={userRole}
        firmId={firmId}
        onNavigateToPersonalNotificationSettings={navigateToPersonalNotificationSettings}
      />

      <div className="main-container">
        {!isMobile && <Sidebar 
          sidebarOpen={sidebarOpen}
          activeTab={activeTab}
          setActiveTab={(tab) => { setActiveTab(tab); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          userRole={userRole}
          userPermissions={userPermissions}
          planFeatures={planFeatures}
        />}
        {!isMobile && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} style={{ display: 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />}

        <main className="main-content" style={{
          ...(activeTab === 'ai-assistant' ? { padding: 0, display: 'flex', flexDirection: 'column' } : {}),
          ...(isMobile ? { paddingBottom: '70px' } : {})
        }}>
          {activeTab !== 'ai-assistant' && (
            <TrialBanner subStatus={subStatus} firmProfile={firmProfile} setActiveTab={setActiveTab} />
          )}
          <ExpiredSubscriptionBanner subStatus={subStatus} userRole={userRole}
            onRenew={() => { setAdminOnboardingDeepLink({ tab: 'subscription', section: null }); setActiveTab('admin'); }} />
          {!invitesDismissed && pendingInvites.length > 0 && activeTab !== 'ai-assistant' && (
            <div style={{ margin: '0 0 1rem' }}>
              <PendingInvitations
                invites={pendingInvites}
                currentFirmId={user?.userProfile?.status === 'removed' ? null : (firmId || null)}
                currentFirmName={firmProfile?.name || ''}
                displayName={user?.displayName || ''}
                onAccepted={() => window.location.reload()}
                onDismiss={() => setInvitesDismissed(true)}
              />
            </div>
          )}
          <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}>
            <ErrorBoundary name="Dashboard">
              <Dashboard recentCases={recentCases} upcomingTasks={upcomingTasks} userId={user.uid} firmId={firmId} setActiveTab={setActiveTab} user={user} onCaseCreated={() => { loadData(false); triggerDataRefresh(); }} dataVersion={dataVersion} userRole={userRole} userPermissions={userPermissions} planFeatures={planFeatures} activeTab={activeTab} onNavigateToPersonalNotificationSettings={navigateToPersonalNotificationSettings} />
            </ErrorBoundary>
          </div>
          <div style={{ display: activeTab === 'clients' ? 'block' : 'none' }}>
            <ErrorBoundary name="Clients">
              <Suspense fallback={<TabLoader />}>
                <ClientManagement userId={user.uid} firmId={firmId} cases={recentCases} userRole={userRole} firmProfile={firmProfile} userProfile={userProfile} dataVersion={dataVersion} />
              </Suspense>
            </ErrorBoundary>
          </div>
          <div style={{ display: activeTab === 'billing' ? 'block' : 'none' }}>
            <ErrorBoundary name="Billing">
              <Suspense fallback={<TabLoader />}>
                <Billing userId={user.uid} cases={recentCases} user={user} firmId={firmId} firmProfile={firmProfile} userProfile={userProfile} dataVersion={dataVersion} />
              </Suspense>
            </ErrorBoundary>
          </div>
          <div style={{ display: activeTab === 'team' ? 'block' : 'none' }}>
            <ErrorBoundary name="Team Hub">
              <Suspense fallback={<TabLoader />}>
                {getPlanFeatures(firmProfile, userProfile).teamHub
                  ? <TeamHub userId={user.uid} firmId={firmId} cases={recentCases} userRole={userRole} user={user} firmProfile={firmProfile} dataVersion={dataVersion} />
                  : <UpgradePrompt feature="Team Hub" requiredPlan="Firm" setActiveTab={setActiveTab} />
                }
              </Suspense>
            </ErrorBoundary>
          </div>
          <div style={{ display: activeTab === 'documents' ? 'block' : 'none' }}>
            <ErrorBoundary name="Documents">
              <Suspense fallback={<TabLoader />}>
                <DocumentManagement userId={user.uid} firmId={firmId} cases={recentCases} userPermissions={userPermissions} firmProfile={firmProfile} userProfile={userProfile} />
              </Suspense>
            </ErrorBoundary>
          </div>
          <div style={{ display: activeTab === 'ai-assistant' ? 'flex' : 'none', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}>
            <ErrorBoundary name="AI Assistant">
              <Suspense fallback={<TabLoader />}>
              <Paralegal
                userId={user.uid}
                firmId={firmId}
                recentCases={recentCases}
                upcomingTasks={upcomingTasks}
                user={user}
                setActiveTab={setActiveTab}
                onDataRefresh={() => { loadData(); triggerDataRefresh(); }}
                gcalConnected={gcalConnected}
                gcalInited={gcalInited}
                setGcalConnected={setGcalConnected}
                meetings={meetings}
                firmProfile={firmProfile}
                userProfile={userProfile}
                preselectedCase={preselectedCase}
                setPreselectedCase={setPreselectedCase}
              />
              </Suspense>
            </ErrorBoundary>
          </div>
          {activeTab !== 'dashboard' && activeTab !== 'clients' && activeTab !== 'billing' && activeTab !== 'team' && activeTab !== 'documents' && activeTab !== 'ai-assistant' && (
            <ErrorBoundary key={activeTab} name={activeTab}>
              <Suspense fallback={<TabLoader />}>
                {renderContent()}
              </Suspense>
            </ErrorBoundary>
          )}
        </main>
      </div>
      {isMobile && <BottomTabBar activeTab={activeTab} setActiveTab={setActiveTab} />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .firmassist {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #080a10;
          min-height: 100vh;
          color: #e8eaed;
          overflow-x: hidden;
        }

        /* Header */
        .header {
          background: rgba(15, 18, 25, 0.97);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .menu-toggle {
          background: none;
          border: none;
          color: #e8eaed;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .menu-toggle:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .logo svg {
          color: #FFD700;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
        }

        .logo-name {
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          background: linear-gradient(135deg, #d4a853 0%, #e8c875 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .logo-tagline {
          font-size: 0.7rem;
          color: #888;
          letter-spacing: 1px;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          padding: 0.6rem;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .header-icon:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .header-icon.notification-icon {
          overflow: visible;
        }
        .notification-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #ff4757;
          color: white;
          font-size: 0.7rem;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          text-align: center;
          padding: 0 5px;
          border-radius: 10px;
          font-weight: 600;
          z-index: 2;
          pointer-events: none;
          box-sizing: border-box;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .user-avatar:hover {
          transform: scale(1.05);
        }

        /* Main Container */
        .main-container {
          display: flex;
          height: calc(100vh - 73px);
          overflow-x: hidden;
        }

        /* Sidebar */
        .sidebar {
          background: #0a0d14;
          border-right: 1px solid rgba(255, 255, 255, 0.06);
          width: 260px;
          padding: 1.5rem 0;
          overflow-y: auto;
          transition: all 0.3s ease;
        }

        .sidebar.closed {
          width: 80px;
        }

        .nav {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0 1rem;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: transparent;
          border: none;
          border-radius: 12px;
          color: #a8adb7;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
          font-weight: 500;
          text-align: left;
        }

        .sidebar.closed .nav-item {
          justify-content: center;
        }

        .sidebar.closed .nav-label {
          display: none;
        }

        .nav-item:hover {
          background: rgba(255, 255, 255, 0.04);
          color: #e8eaed;
        }

        .nav-item.active {
          background: rgba(212, 168, 83, 0.08);
          color: #d4a853;
          border-left: 3px solid #d4a853;
          border-radius: 0 12px 12px 0;
          margin-left: -1rem;
          padding-left: 2rem;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 2rem;
        }

        .content-view {
          max-width: 1400px;
          margin: 0 auto;
        }

        .dashboard-content {
          max-width: 1400px;
          margin: 0 auto;
        }

        .dashboard-header,
        .view-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 1.85rem;
          font-weight: 700;
          color: #e8eaed;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
        }

        .page-subtitle {
          color: #8b8fa3;
          font-size: 0.95rem;
        }

        /* Buttons */
        /* ── Global Select/Dropdown Fix ── */
        select, .firmassist select {
          background-color: #0f1219 !important;
          color: #e8eaed !important;
          appearance: auto;
        }
        select option {
          background-color: #0f1219;
          color: #e8eaed;
          padding: 8px 12px;
        }
        select option:checked,
        select option:hover {
          background-color: #2a2d3a;
          color: #ffffff;
        }
        /* Date inputs */
        input[type="date"],
        input[type="time"],
        input[type="datetime-local"] {
          color-scheme: dark;
        }
        /* Placeholder visibility */
        select:invalid,
        select option[value=""] {
          color: #8b8fa3;
        }
        select:not(:invalid) {
          color: #e8eaed;
        }

        .btn-primary {
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          color: #080a10;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
          letter-spacing: 0.01em;
        }

        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(212, 168, 83, 0.3);
          filter: brightness(1.1);
        }

        .btn-secondary {
          background: transparent;
          color: #d4a853;
          border: 1px solid rgba(212, 168, 83, 0.3);
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background: rgba(212, 168, 83, 0.08);
          border-color: rgba(212, 168, 83, 0.5);
        }

        .btn-ghost {
          background: none;
          border: none;
          color: #d4a853;
          font-weight: 600;
          cursor: pointer;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .btn-ghost:hover {
          background: rgba(255, 215, 0, 0.1);
        }

        .btn-filter {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          padding: 0.6rem 1rem;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .btn-filter:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .btn-icon {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .btn-icon:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .btn-action {
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-action:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(212, 168, 83, 0.3);
        }

        .full-width {
          width: 100%;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: #0f1219;
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          border-color: rgba(212, 168, 83, 0.2);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #e8eaed;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          color: #a8adb7;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .stat-change {
          font-size: 0.85rem;
          font-weight: 600;
        }

        .stat-change.positive {
          color: #10b981;
        }

        .stat-change.negative {
          color: #ef4444;
        }

        /* Dashboard Grid */
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .dashboard-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.3s ease;
        }

        .dashboard-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .dashboard-card.full-width {
          grid-column: 1 / -1;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .card-header h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #e8eaed;
        }

        /* Task List */
        .task-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .task-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .task-item:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .task-checkbox input {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .task-details {
          flex: 1;
        }

        .task-title {
          color: #e8eaed;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }

        .task-meta {
          color: #a8adb7;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .task-separator {
          color: #555;
        }

        .task-due {
          color: #FFD700;
          font-weight: 600;
          font-size: 0.9rem;
        }

        /* Case List */
        .case-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .case-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .case-item:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .case-info {
          flex: 1;
        }

        .case-name {
          color: #e8eaed;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .case-meta {
          color: #a8adb7;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .case-separator {
          color: #555;
        }

        .case-status-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .status-badge {
          padding: 0.35rem 0.75rem;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .status-badge.pending {
          background: rgba(251, 191, 36, 0.2);
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.3);
        }

        .status-badge.closed {
          background: rgba(156, 163, 175, 0.2);
          color: #9ca3af;
          border: 1px solid rgba(156, 163, 175, 0.3);
        }

        .priority-badge {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .priority-badge.high {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .priority-badge.medium {
          background: rgba(251, 191, 36, 0.2);
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.3);
        }

        .priority-badge.low {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        /* Practice Areas */
        .practice-areas {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .practice-area-item {
          display: grid;
          grid-template-columns: 1fr 3fr auto;
          gap: 1rem;
          align-items: center;
        }

        .practice-area-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .practice-area-name {
          color: #e8eaed;
          font-weight: 600;
        }

        .practice-area-stats {
          color: #a8adb7;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .separator {
          color: #555;
        }

        .practice-area-bar {
          background: rgba(255, 255, 255, 0.05);
          height: 10px;
          border-radius: 5px;
          overflow: hidden;
        }

        .practice-area-fill {
          height: 100%;
          background: linear-gradient(90deg, #d4a853 0%, #b8862d 100%);
          border-radius: 5px;
          transition: width 0.5s ease;
        }

        .practice-area-percentage {
          color: #FFD700;
          font-weight: 600;
          text-align: right;
          min-width: 50px;
        }

        /* Document List */
        .document-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .document-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .document-item:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .document-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .document-info {
          flex: 1;
        }

        .document-name {
          color: #e8eaed;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }

        .document-meta {
          color: #a8adb7;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* Filters Bar */
        .filters-bar {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          display: flex;
          align-items: center;
          gap: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 0 1rem;
        }

        .search-box input {
          flex: 1;
          background: none;
          border: none;
          color: #e8eaed;
          padding: 0.75rem 0;
          font-size: 0.95rem;
          outline: none;
        }

        .search-box input::placeholder {
          color: #666;
        }

        .filter-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        /* Cases Table */
        .cases-table {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 2rem;
        }

        .cases-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .cases-table thead {
          background: rgba(255, 255, 255, 0.05);
        }

        .cases-table th {
          padding: 1rem;
          text-align: left;
          color: #a8adb7;
          font-weight: 600;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .cases-table td {
          padding: 1rem;
          color: #e8eaed;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .cases-table tbody tr {
          transition: all 0.3s ease;
        }

        .cases-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .table-case-name {
          font-weight: 600;
        }

        /* Feature Highlights */
        .feature-highlights {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .feature-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.3);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .feature-card svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .feature-card h3 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.25rem;
        }

        .feature-card p {
          color: #a8adb7;
          line-height: 1.6;
        }

        /* Document Stats Grid */
        .document-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .doc-stat-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .doc-stat-card:hover {
          transform: translateY(-3px);
          background: rgba(255, 255, 255, 0.08);
        }

        .doc-stat-card svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .doc-stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #e8eaed;
          margin-bottom: 0.5rem;
        }

        .doc-stat-label {
          color: #a8adb7;
          font-size: 0.9rem;
        }

        /* Document Features */
        .document-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .feature-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
        }

        .feature-section h3 {
          color: #FFD700;
          margin-bottom: 1rem;
          font-size: 1.25rem;
        }

        .feature-section p {
          color: #a8adb7;
          margin-bottom: 1rem;
          line-height: 1.6;
        }

        .feature-section ul {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .feature-section li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          color: #e8eaed;
        }

        .feature-section li svg {
          color: #10b981;
          margin-top: 0.15rem;
          flex-shrink: 0;
        }

        /* Legal Research */
        .research-search {
          margin-bottom: 3rem;
        }

        .large-search-box {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          background: rgba(255, 255, 255, 0.08);
          border: 2px solid rgba(255, 215, 0, 0.3);
          border-radius: 16px;
          padding: 0 1.5rem;
          transition: all 0.3s ease;
        }

        .large-search-box:focus-within {
          border-color: rgba(255, 215, 0, 0.5);
          box-shadow: 0 10px 40px rgba(255, 215, 0, 0.2);
        }

        .large-search-box svg {
          color: #FFD700;
        }

        .large-search-input {
          flex: 1;
          background: none;
          border: none;
          color: #e8eaed;
          padding: 1.5rem 0;
          font-size: 1.1rem;
          outline: none;
        }

        .large-search-input::placeholder {
          color: #666;
        }

        .research-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }

        .category-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .category-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.3);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .category-card svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .category-card h3 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.25rem;
        }

        .category-card p {
          color: #a8adb7;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .research-features {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
        }

        .research-features h3 {
          color: #FFD700;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .features-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .research-feature {
          display: flex;
          gap: 1rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .research-feature:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .research-feature svg {
          color: #10b981;
          flex-shrink: 0;
          margin-top: 0.25rem;
        }

        .research-feature h4 {
          color: #e8eaed;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
        }

        .research-feature p {
          color: #a8adb7;
          line-height: 1.5;
        }

        /* Billing */
        .billing-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .billing-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: all 0.3s ease;
        }

        .billing-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
        }

        .billing-card.primary svg {
          color: #d4a853;
        }

        .billing-card.success svg {
          color: #10b981;
        }

        .billing-card.warning svg {
          color: #fbbf24;
        }

        .billing-card.info svg {
          color: #3b82f6;
        }

        .billing-card svg {
          margin-bottom: 1rem;
        }

        .billing-value {
          font-size: 2rem;
          font-weight: 700;
          color: #e8eaed;
          margin-bottom: 0.5rem;
        }

        .billing-label {
          color: #a8adb7;
          font-size: 0.9rem;
        }

        .billing-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .billing-feature-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
        }

        .billing-feature-card h3 {
          color: #FFD700;
          margin-bottom: 1.5rem;
          font-size: 1.25rem;
        }

        .billing-feature-card ul {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .billing-feature-card li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          color: #e8eaed;
        }

        .billing-feature-card li svg {
          color: #10b981;
          margin-top: 0.15rem;
          flex-shrink: 0;
        }

        /* Analytics */
        .analytics-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .metric-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-3px);
          background: rgba(255, 255, 255, 0.08);
        }

        .metric-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          color: #a8adb7;
          font-size: 0.9rem;
        }

        .trend-icon {
          color: #666;
        }

        .trend-icon.positive {
          color: #10b981;
        }

        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #e8eaed;
          margin-bottom: 0.5rem;
        }

        .metric-change {
          font-size: 0.9rem;
          font-weight: 600;
        }

        .metric-change.positive {
          color: #10b981;
        }

        .metric-change.negative {
          color: #ef4444;
        }

        .metric-change.neutral {
          color: #a8adb7;
        }

        .analytics-insights {
          margin-top: 2rem;
        }

        .analytics-insights h3 {
          color: #FFD700;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .insight-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          transition: all 0.3s ease;
        }

        .insight-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.3);
        }

        .insight-card svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .insight-card h4 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.1rem;
        }

        .insight-card p {
          color: #a8adb7;
          line-height: 1.6;
        }

        /* Conflict Check */
        .conflict-check-box {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .conflict-check-box h3 {
          color: #FFD700;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .conflict-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .conflict-input {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 1rem;
          color: #e8eaed;
          font-size: 1rem;
          outline: none;
          transition: all 0.3s ease;
        }

        .conflict-input:focus {
          border-color: rgba(255, 215, 0, 0.3);
          background: rgba(255, 255, 255, 0.08);
        }

        .conflict-input::placeholder {
          color: #666;
        }

        .conflict-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .conflict-feature {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .conflict-feature:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
        }

        .conflict-feature svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .conflict-feature h4 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.1rem;
        }

        .conflict-feature p {
          color: #a8adb7;
          line-height: 1.6;
        }

        /* Client Portal */
        .portal-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .portal-feature {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .portal-feature:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.3);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .portal-feature svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .portal-feature h3 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.25rem;
        }

        .portal-feature p {
          color: #a8adb7;
          line-height: 1.6;
        }

        /* Task & Calendar */
        .task-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .task-feature-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .task-feature-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.3);
        }

        .task-feature-card svg {
          color: #FFD700;
          margin-bottom: 1rem;
        }

        .task-feature-card h3 {
          color: #e8eaed;
          margin-bottom: 0.75rem;
          font-size: 1.25rem;
        }

        .task-feature-card p {
          color: #a8adb7;
          line-height: 1.6;
        }

        .upcoming-calendar {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 2rem;
        }

        .upcoming-calendar h3 {
          color: #FFD700;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .calendar-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .calendar-item {
          display: flex;
          gap: 1.5rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .calendar-item:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .calendar-date {
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          border-radius: 12px;
          padding: 1rem;
          min-width: 60px;
          text-align: center;
        }

        .date-day {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.25rem;
        }

        .date-month {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .calendar-details {
          flex: 1;
        }

        .calendar-title {
          color: #e8eaed;
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
        }

        .calendar-meta {
          color: #a8adb7;
          font-size: 0.9rem;
        }

        /* Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(255, 215, 0, 0.3);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 215, 0, 0.5);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .sidebar.open {
            position: absolute;
            height: 100%;
            z-index: 50;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 0.75rem;
          }

          .page-title {
            font-size: 1.3rem;
          }

          .page-subtitle {
            font-size: 0.8rem;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }

          .stat-card {
            padding: 0.75rem;
          }

          .stat-value {
            font-size: 1.2rem;
          }

          .stat-icon {
            width: 36px;
            height: 36px;
          }

          .stat-icon svg {
            width: 18px;
            height: 18px;
          }

          .filters-bar {
            flex-direction: column;
          }

          .search-box {
            min-width: 100%;
          }

          .cases-table {
            overflow-x: auto;
            font-size: 0.85rem;
          }

          .cases-table th, .cases-table td {
            padding: 0.5rem 0.4rem;
            font-size: 0.8rem;
          }

          .sidebar {
            width: 0;
            overflow: hidden;
          }

          .sidebar.open {
            width: 260px;
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 100;
            box-shadow: 4px 0 20px rgba(0,0,0,0.5);
          }

          .sidebar .nav-label {
            display: none;
          }

          .sidebar.open .nav-label {
            display: block;
          }

          .header {
            padding: 0.75rem 1rem;
          }

          .logo-text {
            display: none;
          }

          .view-header {
            flex-direction: column;
            gap: 0.75rem;
            align-items: flex-start;
          }

          .view-header > div:last-child {
            width: 100%;
            flex-wrap: wrap;
          }

          .card {
            border-radius: 10px;
          }

          .card-header {
            padding: 1rem;
          }

          .task-item {
            padding: 0.75rem 1rem;
          }

          .header-dropdown {
            right: -1rem;
            max-width: calc(100vw - 2rem);
          }

          .btn-primary, .btn-secondary {
            font-size: 0.85rem;
            padding: 0.5rem 0.75rem;
          }

          .sidebar-overlay {
            display: block !important;
          }
        }

        @media (max-width: 480px) {
          .main-content {
            padding: 0.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .page-title {
            font-size: 1.15rem;
          }

          .view-header {
            padding: 0;
          }

          .card {
            border-radius: 8px;
          }

          .btn-primary, .btn-secondary {
            font-size: 0.8rem;
            padding: 0.45rem 0.65rem;
          }
        }

        /* ═══════════════════════════════════════════════ */
        /* COMPREHENSIVE MOBILE RESPONSIVE OVERRIDES     */
        /* ═══════════════════════════════════════════════ */

        /* Force all grids to single column on mobile */
        @media (max-width: 768px) {
          /* Universal grid fix — catches ALL inline grid layouts */
          div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
          }

          /* Modal fixes — full width on mobile */
          .modal-content,
          div[style*="maxWidth: '600px'"],
          div[style*="maxWidth: '700px'"],
          div[style*="maxWidth: '800px'"],
          div[style*="maxWidth: '900px'"],
          div[style*="maxWidth: '1000px'"],
          div[style*="maxWidth: '1100px'"] {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
          }

          /* Modal overlay padding */
          .modal-overlay,
          div[style*="position: 'fixed'"][style*="alignItems: 'center'"] {
            padding: 0.75rem !important;
          }

          /* Modal internal padding */
          div[style*="padding: '2rem'"] {
            padding: 1rem !important;
          }

          div[style*="padding: '1.5rem 2rem'"] {
            padding: 1rem !important;
          }

          div[style*="padding: '2rem 2.5rem'"] {
            padding: 1rem !important;
          }

          /* Fix flex rows that should wrap */
          div[style*="display: 'flex'"][style*="gap"] {
            flex-wrap: wrap !important;
          }

          /* Subscription plan cards */
          div[style*="maxWidth: '900px'"],
          div[style*="maxWidth: '1100px'"] {
            max-width: 100% !important;
          }

          /* Header fixes */
          .header {
            padding: 0.5rem 0.75rem !important;
          }

          .header > div {
            gap: 0.5rem !important;
          }

          /* AI Chat panel mobile optimization */
          .ai-assistant-container {
            height: calc(100vh - 60px) !important;
          }

          .ai-header {
            padding: 0.75rem 1rem !important;
          }

          .ai-icon {
            width: 36px !important;
            height: 36px !important;
          }

          .ai-title {
            font-size: 1.1rem !important;
          }

          .quick-actions {
            padding: 0.75rem 1rem !important;
          }

          .quick-actions-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 0.5rem !important;
          }

          .quick-action-btn {
            padding: 0.5rem 0.75rem !important;
            font-size: 0.8rem !important;
          }

          .messages-container {
            padding: 1rem !important;
            gap: 1rem !important;
          }

          .message-content {
            max-width: 85% !important;
          }

          .message-text {
            padding: 0.85rem !important;
            font-size: 0.9rem !important;
          }

          .message-avatar {
            width: 32px !important;
            height: 32px !important;
          }

          .message {
            gap: 0.5rem !important;
          }

          /* AI chat history panel */
          div[style*="width: '320px'"][style*="position: 'absolute'"] {
            width: calc(100vw - 2rem) !important;
            right: 0.5rem !important;
            left: 0.5rem !important;
          }

          /* Chat input area */
          .chat-input-form,
          div[style*="borderTop: '1px solid rgba(255,255,255,0.06)'"][style*="padding"] {
            padding: 0.75rem !important;
          }

          /* Tables */
          table {
            font-size: 0.78rem !important;
          }

          th, td {
            padding: 0.4rem 0.35rem !important;
          }

          /* Hide non-essential table columns on small screens */
          .cases-table th:nth-child(n+5),
          .cases-table td:nth-child(n+5) {
            display: none;
          }

          /* Tab/filter bars */
          div[style*="display: 'flex'"][style*="borderBottom"] {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            flex-wrap: nowrap !important;
          }

          /* Buttons */
          button[style*="padding: '0.7rem 1.5rem'"] {
            padding: 0.6rem 1rem !important;
            font-size: 0.85rem !important;
          }

          button[style*="padding: '0.85rem'"] {
            padding: 0.7rem !important;
          }

          /* Page header */
          h1[style*="fontSize: '1.8rem'"],
          h1[style*="fontSize: '1.6rem'"] {
            font-size: 1.2rem !important;
          }

          h2[style*="fontSize: '1.5rem'"],
          h2[style*="fontSize: '1.4rem'"] {
            font-size: 1.1rem !important;
          }

          /* Card padding reduction */
          .card-body,
          div[style*="padding: '1.5rem'"] {
            padding: 1rem !important;
          }

          /* Analytics/report grids */
          div[style*="gridTemplateColumns: 'repeat(4"] {
            grid-template-columns: 1fr 1fr !important;
          }

          /* Notification/dropdown panels */
          .header-dropdown {
            position: fixed !important;
            top: 60px !important;
            left: 0.5rem !important;
            right: 0.5rem !important;
            max-width: none !important;
            width: auto !important;
            max-height: 70vh !important;
          }

          /* Client portal mobile */
          div[style*="maxWidth: '1400px'"] {
            max-width: 100% !important;
            padding: 0.5rem !important;
          }

          /* WhatsApp template inputs */
          textarea[style*="letterSpacing"] {
            font-size: 0.9rem !important;
          }

          /* Print/receipt buttons row */
          div[style*="display: 'flex'"][style*="justifyContent: 'flex-end'"] {
            flex-wrap: wrap !important;
          }

          /* Billing/payment form grids */
          div[style*="display: 'grid'"][style*="gap: '1.25rem'"] {
            grid-template-columns: 1fr !important;
          }

          /* Document upload area */
          div[style*="border: '2px dashed'"] {
            padding: 1.5rem 1rem !important;
          }

          /* Calendar view */
          div[style*="display: 'grid'"][style*="gap: '2rem'"] {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }

          /* Team hub cards */
          div[style*="display: 'grid'"][style*="gap: '1rem'"] {
            grid-template-columns: 1fr !important;
          }

          /* Admin panel sections */
          div[style*="display: 'grid'"][style*="gap: '0.75rem'"] {
            grid-template-columns: 1fr !important;
          }

          /* Fix overflow on the main app container */
          #root, .app-layout {
            overflow-x: hidden !important;
          }

          body {
            overflow-x: hidden !important;
          }

          /* Fix any fixed-width elements */
          div[style*="width: '300px'"],
          div[style*="width: '320px'"],
          div[style*="width: '400px'"] {
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Reduce section spacing */
          div[style*="marginBottom: '1.25rem'"] {
            margin-bottom: 0.75rem !important;
          }

          div[style*="marginBottom: '2rem'"] {
            margin-bottom: 1rem !important;
          }

          /* Login/Signup page */
          div[style*="maxWidth: '500px'"] {
            max-width: 100% !important;
            padding: 1.5rem 1rem !important;
          }

          /* Onboarding/Pricing cards on signup */
          div[style*="gridTemplateColumns: 'repeat(3, 1fr)'"] {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 480px) {
          /* Even more aggressive on small phones */

          .quick-actions-grid {
            grid-template-columns: 1fr !important;
          }

          .message-content {
            max-width: 90% !important;
          }

          .message-text {
            padding: 0.7rem !important;
            font-size: 0.85rem !important;
          }

          /* Stat cards side by side is still too tight */
          .stats-grid {
            grid-template-columns: 1fr !important;
            gap: 0.4rem !important;
          }

          .stat-card {
            padding: 0.6rem !important;
          }

          /* Tab buttons scroll */
          button[style*="padding: '0.6rem 1.5rem'"] {
            padding: 0.5rem 0.8rem !important;
            font-size: 0.8rem !important;
            white-space: nowrap !important;
          }

          /* Ultra-compact header */
          .header {
            padding: 0.4rem 0.5rem !important;
          }

          /* Hide less important action buttons text */
          .btn-clear span {
            display: none;
          }

          /* Compact cards */
          div[style*="padding: '1.5rem'"],
          div[style*="padding: '1.25rem'"] {
            padding: 0.75rem !important;
          }

          /* Very small font for tables */
          table {
            font-size: 0.72rem !important;
          }

          th, td {
            padding: 0.3rem 0.25rem !important;
          }

          /* Fix pricing cards on signup being too padded */
          div[style*="padding: '2rem'"][style*="borderRadius"] {
            padding: 1rem !important;
          }

          /* Full-width buttons on small screens */
          .btn-primary, .btn-secondary {
            width: 100% !important;
            justify-content: center !important;
          }
        }

        /* Fix horizontal scroll globally */
        html, body {
          overflow-x: hidden;
          max-width: 100vw;
        }

        /* Client Portal mobile fixes */
        @media (max-width: 600px) {
          /* Portal progress tracker — make stages smaller */
          div[style*="width: '32px'"][style*="borderRadius: '50%'"] {
            width: 26px !important;
            height: 26px !important;
            font-size: 0.65rem !important;
          }

          /* Portal header */
          div[style*="padding: '2rem 1.5rem'"] h1 {
            font-size: 1.2rem !important;
          }

          /* Portal stage labels */
          div[style*="fontSize: '0.65rem'"] {
            font-size: 0.55rem !important;
          }

          /* Portal billing table */
          div[style*="maxWidth: '800px'"] table {
            font-size: 0.75rem !important;
          }

          div[style*="maxWidth: '800px'"] th,
          div[style*="maxWidth: '800px'"] td {
            padding: 0.4rem 0.3rem !important;
          }
        }

        /* ═══════════════════════════════════════════════ */
        /* BOTTOM TAB BAR — MOBILE LAYOUT OVERRIDES      */
        /* ═══════════════════════════════════════════════ */

        @media (max-width: 768px) {
          /* Hide hamburger menu on mobile — bottom bar replaces it */
          .menu-toggle {
            display: none !important;
          }

          /* AI chat needs bottom padding for tab bar */
          .ai-assistant-container {
            height: calc(100vh - 130px) !important;
          }

          /* Main content bottom padding for tab bar */
          .main-content {
            padding-bottom: 70px !important;
          }

          /* No sidebar at all on mobile */
          .sidebar, .sidebar.open, .sidebar.closed {
            display: none !important;
            width: 0 !important;
          }

          /* Header logo can be more compact */
          .header .logo-area {
            gap: 0.4rem !important;
          }

          /* Case split-pane mobile fixes */
          .content-view .view-header {
            padding: 0.5rem 0 !important;
          }

          .content-view .stats-grid {
            display: none !important;
          }

          /* Edit tab party rows stack on phone */
          div[style*="flex"][style*="gap: '0.4rem'"][style*="alignItems: 'center'"] input[style*="flex: 1"] {
            min-width: 0 !important;
          }
        }

        @media (max-width: 480px) {
          /* Even more compact header on small phones */
          .header {
            padding: 0.35rem 0.5rem !important;
          }

          /* Tab buttons in case workspace scrollable */
          div[style*="borderBottom"][style*="background: 'rgba(0,0,0,0.15)'"] {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }

          div[style*="borderBottom"][style*="background: 'rgba(0,0,0,0.15)'"] button {
            white-space: nowrap !important;
            min-width: 0 !important;
            padding: 0.5rem 0.3rem !important;
            font-size: 0.72rem !important;
          }
        }

        /* Smooth transitions for responsive changes */
        @media (max-width: 768px) {
          *, *::before, *::after {
            transition: none !important;
            animation-duration: 0.01ms !important;
          }
        }

        /* AI Assistant Styles */
        .ai-assistant-container {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 73px);
          background: rgba(255, 255, 255, 0.02);
        }

        .ai-header {
          background: rgba(15, 18, 25, 0.95);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 1.5rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .ai-header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .ai-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .ai-title {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #d4a853 0%, #e8c875 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.25rem;
        }

        .ai-subtitle {
          color: #a8adb7;
          font-size: 0.9rem;
        }

        .btn-clear {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-clear:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .quick-actions {
          padding: 1.5rem 2rem;
          background: rgba(255, 255, 255, 0.03);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .quick-actions-label {
          color: #a8adb7;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 1rem;
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .quick-action-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          padding: 0.75rem 1rem;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .quick-action-btn:hover {
          background: rgba(212, 168, 83, 0.2);
          border-color: rgba(212, 168, 83, 0.3);
          transform: translateY(-2px);
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .message {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }

        .user-message {
          flex-direction: row-reverse;
        }

        .message-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .user-avatar {
          background: linear-gradient(135deg, #d4a853 0%, #e8c875 100%);
          color: #080a10;
          font-weight: 600;
          font-size: 0.75rem;
        }

        .message-content {
          flex: 1;
          max-width: 70%;
        }

        .user-message .message-content {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .message-text {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 1.25rem;
          border-radius: 12px;
          color: #e8eaed;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        .user-message .message-text {
          background: linear-gradient(135deg, rgba(212, 168, 83, 0.2) 0%, rgba(184, 134, 45, 0.2) 100%);
          border-color: rgba(212, 168, 83, 0.3);
        }

        .message-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }

        .message-action-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #a8adb7;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }

        .message-action-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #e8eaed;
          border-color: rgba(255, 255, 255, 0.2);
        }

        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #a8adb7;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .input-container {
          padding: 1.5rem 2rem;
          background: rgba(15, 18, 25, 0.95);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          gap: 1rem;
        }

        .ai-input {
          flex: 1;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 1rem;
          color: #e8eaed;
          font-size: 0.95rem;
          resize: none;
          outline: none;
          font-family: inherit;
          transition: all 0.3s ease;
        }

        .ai-input:focus {
          border-color: rgba(212, 168, 83, 0.5);
          background: rgba(255, 255, 255, 0.08);
        }

        .ai-input::placeholder {
          color: #666;
        }

        .send-btn {
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .send-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(212, 168, 83, 0.3);
        }

        .send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ai-disclaimer {
          padding: 1rem 2rem;
          background: rgba(255, 193, 7, 0.1);
          border-top: 1px solid rgba(255, 193, 7, 0.3);
          color: #ffc107;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .messages-container::-webkit-scrollbar {
          width: 8px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: rgba(212, 168, 83, 0.3);
          border-radius: 4px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: rgba(212, 168, 83, 0.5);
        }
      `}</style>
    </div>
    </>
    </SubscriptionContext.Provider>
  );
};

export default FirmAssist;